import { useState } from "react";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { PROJECT_TYPES } from "../lib/constants";

export default function ContactForm({ compact = false }) {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        project_type: PROJECT_TYPES[0],
        message: "",
        company: "", // honeypot
    });
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [errorMsg, setErrorMsg] = useState("");

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus("loading");
        setErrorMsg("");
        try {
            await api.post("/contact", form);
            setStatus("success");
            setForm({
                name: "",
                phone: "",
                email: "",
                project_type: PROJECT_TYPES[0],
                message: "",
                company: "",
            });
        } catch (err) {
            setStatus("error");
            setErrorMsg(
                err?.response?.data?.detail ||
                    "Something went wrong. Please try again or call us directly.",
            );
        }
    };

    if (status === "success") {
        return (
            <div
                data-testid="contact-success"
                className="border border-[#F5A623]/40 bg-[#0F0F0F] p-10 text-center"
            >
                <CheckCircle2 className="mx-auto text-[#F5A623]" size={42} />
                <h3 className="font-display text-2xl mt-5 text-white">
                    Inquiry Received
                </h3>
                <p className="text-white/60 mt-3 max-w-md mx-auto leading-relaxed">
                    Thank you. A member of our team will reach out within 24
                    hours to schedule your consult.
                </p>
                <button
                    onClick={() => setStatus("idle")}
                    className="btn-secondary mt-8"
                    data-testid="contact-send-another"
                >
                    Send Another
                </button>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className={`${compact ? "" : "bg-[#0A0A0A] border border-white/10 p-8 md:p-10"}`}
        >
            {/* Honeypot */}
            <input
                type="text"
                value={form.company}
                onChange={update("company")}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
                name="company-field"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="block">
                    <span className="overline">Name</span>
                    <input
                        type="text"
                        required
                        value={form.name}
                        onChange={update("name")}
                        data-testid="contact-input-name"
                        className="input-bevel mt-2"
                        placeholder="Full name"
                    />
                </label>
                <label className="block">
                    <span className="overline">Phone</span>
                    <input
                        type="tel"
                        required
                        value={form.phone}
                        onChange={update("phone")}
                        data-testid="contact-input-phone"
                        className="input-bevel mt-2"
                        placeholder="(918) 000-0000"
                    />
                </label>
                <label className="block">
                    <span className="overline">Email</span>
                    <input
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        data-testid="contact-input-email"
                        className="input-bevel mt-2"
                        placeholder="you@email.com"
                    />
                </label>
                <label className="block">
                    <span className="overline">Project Type</span>
                    <select
                        required
                        value={form.project_type}
                        onChange={update("project_type")}
                        data-testid="contact-select-project-type"
                        className="input-bevel mt-2 appearance-none bg-no-repeat bg-[length:14px] bg-[right_1rem_center]"
                        style={{
                            backgroundImage:
                                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23F5A623' stroke-width='2.5' stroke-linecap='square'><polyline points='6 9 12 15 18 9'/></svg>\")",
                        }}
                    >
                        {PROJECT_TYPES.map((p) => (
                            <option key={p} value={p}>
                                {p}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <label className="block mt-5">
                <span className="overline">Message</span>
                <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-input-message"
                    className="input-bevel mt-2 resize-none"
                    placeholder="Tell us about your project, timeline, and goals."
                />
            </label>

            {status === "error" && (
                <div
                    data-testid="contact-error"
                    className="mt-5 text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-4 py-3"
                >
                    {errorMsg}
                </div>
            )}

            <button
                type="submit"
                disabled={status === "loading"}
                data-testid="contact-submit-button"
                className="btn-primary w-full md:w-auto mt-8 disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {status === "loading" ? (
                    <>
                        <Loader2 className="animate-spin" size={16} /> Sending...
                    </>
                ) : (
                    <>
                        <Send size={16} /> Send Inquiry
                    </>
                )}
            </button>
        </form>
    );
}
